import React, { useState, useMemo } from 'react';
import { Typography, Spin, Switch, App, Popconfirm } from 'antd';
import { LoadingOutlined, SoundOutlined, PlayCircleOutlined, CodepenOutlined, DeleteOutlined } from '@ant-design/icons';
import { useCanvasGeneratedMedia, useDeleteGeneratedMedia } from '../../../api/canvasApi';

const { Text } = Typography;

const CanvasMediaGallery = ({ canvasId, onMediaClick, nodes = [] }) => {
    const { message } = App.useApp();
    const { data: media = [], isLoading } = useCanvasGeneratedMedia(canvasId);
    const deleteMediaMutation = useDeleteGeneratedMedia(canvasId);
    const [showOnCanvasOnly, setShowOnCanvasOnly] = useState(false);
    const [deletingId, setDeletingId] = useState(null);

    const handleDeleteMedia = async (e, mediaId) => {
        e.stopPropagation();
        setDeletingId(mediaId);
        try {
            await deleteMediaMutation.mutateAsync(mediaId);
            message.success('Media deleted');
        } catch (error) {
            console.error('Failed to delete media:', error);
            message.error('Failed to delete media');
        }
        setDeletingId(null);
    };

    // Build a set of URLs that are currently placed on the canvas
    const placedUrls = useMemo(() => {
        const urls = new Set();
        nodes.forEach(node => {
            if (node.data?.src) {
                // Normalize URL by removing query parameters for comparison
                const cleanUrl = node.data.src.split('?')[0];
                urls.add(cleanUrl);
                urls.add(node.data.src); // Also add original URL
            }
        });
        return urls;
    }, [nodes]);

    // Don't render if no media
    if (!isLoading && media.length === 0) return null;

    // Transform media items for display
    const allDisplayMedia = media.map(item => {
        let type = item.type || 'image';
        // Fix extension extraction to handle URLs with query parameters
        const cleanUrl = item.url ? item.url.split('?')[0] : '';
        const ext = cleanUrl ? cleanUrl.split('.').pop().toLowerCase() : '';

        if (ext === 'glb' || ext === 'gltf') {
            type = 'model';
        }

        // Check if this media is placed on canvas
        const isOnCanvas = placedUrls.has(cleanUrl) || placedUrls.has(item.url);

        return {
            id: item.id,
            src: item.url,
            type: type,
            name: item.prompt?.slice(0, 30) || 'Generated Media',
            // Enrich with metadata for preview
            prompt: item.prompt || '',
            model: item.model || '',
            provider: item.provider || '',
            metadata: item.metadata || {},
            createdAt: item.created_at || item.createdAt,
            isOnCanvas
        };
    }).sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

    // Apply filter based on toggle state
    const displayMedia = showOnCanvasOnly
        ? allDisplayMedia.filter(item => item.isOnCanvas)
        : allDisplayMedia;

    const onCanvasCount = allDisplayMedia.filter(item => item.isOnCanvas).length;

    return (
        <div style={{
            background: 'rgba(22, 27, 34, 0.95)',
            borderRadius: 12,
            border: '1px solid #30363d',
            padding: '8px',
            maxWidth: 360,
            backdropFilter: 'blur(8px)',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: 8,
                paddingLeft: 4,
                paddingRight: 4,
            }}>
                <Text style={{ color: '#8b949e', fontSize: 10, fontWeight: 600, letterSpacing: '0.5px' }}>
                    GENERATED MEDIA
                </Text>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 4,
                            cursor: 'pointer',
                            padding: '2px 6px',
                            borderRadius: 4,
                            background: showOnCanvasOnly ? 'rgba(24, 144, 255, 0.15)' : 'transparent',
                            transition: 'background 0.2s'
                        }}
                        onClick={() => setShowOnCanvasOnly(!showOnCanvasOnly)}
                    >
                        <Switch
                            size="small"
                            checked={showOnCanvasOnly}
                            onChange={setShowOnCanvasOnly}
                            style={{
                                background: showOnCanvasOnly ? '#1890ff' : '#30363d',
                                minWidth: 28
                            }}
                        />
                        <Text style={{
                            color: showOnCanvasOnly ? '#1890ff' : '#8b949e',
                            fontSize: 9,
                            fontWeight: 500,
                            whiteSpace: 'nowrap'
                        }}>
                            On Canvas
                        </Text>
                    </div>
                    <Text style={{ color: '#484f58', fontSize: 10 }}>
                        {showOnCanvasOnly ? `${onCanvasCount}` : media.length} items
                    </Text>
                </div>
            </div>

            {/* Gallery */}
            {isLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 20 }}>
                    <Spin indicator={<LoadingOutlined style={{ fontSize: 18, color: '#1890ff' }} spin />} />
                </div>
            ) : (
                <div
                    className="canvas-media-gallery-track"
                    style={{
                        display: 'flex',
                        gap: 8,
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        scrollbarWidth: 'thin',
                        scrollbarColor: '#30363d transparent',
                        paddingBottom: 4,
                        maskImage: 'linear-gradient(to right, black 90%, transparent)',
                        WebkitMaskImage: 'linear-gradient(to right, black 90%, transparent)',
                    }}
                >
                    {displayMedia.map((item, idx) => (
                        <div
                            key={item.id}
                            draggable
                            onDragStart={(e) => {
                                e.dataTransfer.setData('application/json', JSON.stringify({
                                    id: item.id,
                                    type: item.type,
                                    src: item.src,
                                    label: item.name
                                }));
                                e.dataTransfer.effectAllowed = 'move';
                            }}
                            onClick={() => {
                                if (onMediaClick) {
                                    onMediaClick(idx, displayMedia);
                                }
                            }}
                            style={{
                                width: 72,
                                height: 72,
                                flexShrink: 0,
                                borderRadius: 8,
                                overflow: 'hidden',
                                border: '1px solid #30363d',
                                background: '#0d1117',
                                position: 'relative',
                                cursor: 'pointer',
                                transition: 'border-color 0.2s, transform 0.2s',
                            }}
                            className="gallery-item"
                        >
                            {item.type === 'audio' ? (
                                <div style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'linear-gradient(135deg, #1c2128 0%, #161b22 100%)',
                                    color: '#eb2f96'
                                }}>
                                    <SoundOutlined style={{ fontSize: 24 }} />
                                </div>
                            ) : item.type === 'video' ? (
                                <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                    <video
                                        src={item.src}
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                        muted
                                        loop
                                        onMouseOver={e => e.target.play()}
                                        onMouseOut={e => { e.target.pause(); e.target.currentTime = 0; }}
                                    />
                                    <div style={{
                                        position: 'absolute',
                                        top: 4,
                                        right: 4,
                                        background: 'rgba(0,0,0,0.6)',
                                        padding: '2px 4px',
                                        borderRadius: 3,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 2
                                    }}>
                                        <PlayCircleOutlined style={{ fontSize: 8, color: '#fff' }} />
                                    </div>
                                </div>
                            ) : item.type === 'model' ? (
                                <div style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: 'linear-gradient(135deg, #2a1261 0%, #161b22 100%)', // darker purple hint
                                    color: '#b026ff',
                                    position: 'relative'
                                }}>
                                    <CodepenOutlined style={{ fontSize: 28 }} />
                                    <div style={{
                                        position: 'absolute',
                                        bottom: 4,
                                        right: 4,
                                        background: 'rgba(0,0,0,0.6)',
                                        padding: '1px 3px',
                                        borderRadius: 3,
                                        fontSize: 8,
                                        fontWeight: 'bold',
                                        color: '#fff'
                                    }}>
                                        3D
                                    </div>
                                </div>
                            ) : (
                                <img
                                    src={item.src}
                                    alt={item.name}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    draggable={false}
                                />
                            )}
                            {/* Delete button */}
                            <Popconfirm
                                title="Delete media?"
                                description="This will permanently delete this media."
                                onConfirm={(e) => handleDeleteMedia(e, item.id)}
                                onCancel={(e) => e.stopPropagation()}
                                okText="Delete"
                                cancelText="Cancel"
                                okButtonProps={{ danger: true }}
                            >
                                <div
                                    className="delete-btn"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                        position: 'absolute',
                                        bottom: 4,
                                        left: 4,
                                        width: 20,
                                        height: 20,
                                        borderRadius: 4,
                                        background: 'rgba(0,0,0,0.7)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        opacity: 0,
                                        transition: 'opacity 0.2s, background 0.2s',
                                        zIndex: 10
                                    }}
                                >
                                    {deletingId === item.id ? (
                                        <LoadingOutlined style={{ color: '#ff4d4f', fontSize: 10 }} spin />
                                    ) : (
                                        <DeleteOutlined style={{ color: '#ff4d4f', fontSize: 10 }} />
                                    )}
                                </div>
                            </Popconfirm>
                        </div>
                    ))}
                </div>
            )}

            {/* Inline Styles */}
            <style>{`
                .canvas-media-gallery-track::-webkit-scrollbar {
                    height: 4px;
                }
                .canvas-media-gallery-track::-webkit-scrollbar-track {
                    background: transparent;
                }
                .canvas-media-gallery-track::-webkit-scrollbar-thumb {
                    background: #30363d;
                    border-radius: 2px;
                }
                .gallery-item:hover {
                    border-color: #1890ff !important;
                    transform: scale(1.02);
                }
                .gallery-item:hover .delete-btn {
                    opacity: 1 !important;
                }
                .delete-btn:hover {
                    background: rgba(255, 77, 79, 0.3) !important;
                }
            `}</style>
        </div>
    );
};

export default CanvasMediaGallery;
